import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { CheckCircle2, ExternalLink, Gift, Loader2 } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api.js';

/**
 * Admin-only extras kept from the previous Billing page: promo-code redeem,
 * plan subscribe (non-comped tenants) and the Stripe customer portal.
 * @param {{subscription: object}} props
 */
export default function PlansAndPromo({ subscription }) {
  const qc = useQueryClient();
  const { data: plansData } = useQuery({ queryKey: ['stripe-plans'], queryFn: () => apiGet('/stripe/plans'), staleTime: 60_000 });
  const { data: status } = useQuery({ queryKey: ['stripe-status'], queryFn: () => apiGet('/stripe/status'), staleTime: 15_000 });
  const plans = plansData?.data || [];
  const hasCustomer = !!status?.billing?.stripeCustomerId;
  const [promo, setPromo] = useState('');
  const [busy, setBusy] = useState('');

  async function redeem() {
    if (!promo.trim()) return;
    setBusy('promo');
    try {
      const r = await apiPost('/stripe/promo/redeem', { code: promo });
      toast.success(`Promo "${r.promo.code}" applied — ${r.promo.description || ''}`);
      setPromo('');
      qc.invalidateQueries({ queryKey: ['billing-overview'] });
      qc.invalidateQueries({ queryKey: ['stripe-status'] });
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Promo failed');
    } finally { setBusy(''); }
  }
  async function subscribe(slug) {
    setBusy(slug);
    try {
      const r = await apiPost('/stripe/subscribe', { planSlug: slug });
      if (r.data?.checkoutUrl) window.location.href = r.data.checkoutUrl;
      else if (r.data?.mode === 'dev') { toast.success(r.data.message); qc.invalidateQueries({ queryKey: ['billing-overview'] }); }
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Subscribe failed');
    } finally { setBusy(''); }
  }
  async function openPortal() {
    setBusy('portal');
    try {
      const r = await apiPost('/stripe/portal', {});
      if (r.data?.portalUrl) window.location.href = r.data.portalUrl;
    } catch (e) {
      toast.error(e.response?.data?.error || 'Portal failed');
    } finally { setBusy(''); }
  }

  return (
    <section className="panel p-5 space-y-4" data-testid="plans-promo">
      <div>
        <div className="text-sm font-medium mb-2 flex items-center gap-2"><Gift className="w-4 h-4 text-purple-400" /> Promo code</div>
        <div className="flex gap-2 max-w-md">
          <input value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} placeholder="DISRUPTIONAGENTS" className="input flex-1 font-mono" aria-label="Promo code" />
          <button type="button" onClick={redeem} disabled={busy === 'promo' || !promo.trim()} className="btn-primary text-sm disabled:opacity-50">
            {busy === 'promo' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
          </button>
        </div>
      </div>

      {!subscription.comped && plans.length > 0 && (
        <div>
          <div className="font-display font-bold mb-1">Plans</div>
          <div className="grid md:grid-cols-3 gap-3">
            {plans.map((p) => {
              const current = subscription.planSlug === p.slug && subscription.status === 'active';
              return (
                <div key={p.slug} className={`rounded-xl border p-4 ${current ? 'border-cyan-500/50 bg-cyan-500/5' : 'border-white/10 bg-white/5'}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-semibold">{p.name}</div>
                    {current && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300">Current</span>}
                  </div>
                  <div className="text-2xl font-bold mt-2">${p.monthlyPriceUsd}<span className="text-xs text-white/50 font-normal">/mo</span></div>
                  <ul className="text-xs text-white/60 mt-3 space-y-1">
                    {(p.features || []).map((f) => <li key={f} className="flex items-start gap-1.5"><CheckCircle2 className="w-3 h-3 text-emerald-400 mt-0.5 shrink-0" />{f}</li>)}
                  </ul>
                  {!current && (
                    <button type="button" onClick={() => subscribe(p.slug)} disabled={busy === p.slug} className="btn-primary text-xs w-full mt-4 disabled:opacity-50">
                      {busy === p.slug ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Subscribe'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {hasCustomer && (
        <button type="button" onClick={openPortal} disabled={busy === 'portal'} className="btn-ghost text-sm flex items-center gap-2">
          {busy === 'portal' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />} Manage invoices in Stripe
        </button>
      )}
    </section>
  );
}
